import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { Trash2 } from "lucide-react";
import { DateField } from "@/components/admin/date-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";
import { MessageComposer } from "../notes/MessageComposer";
import { NoteAttachments } from "../notes/NoteAttachments";
import type { TaskComment, AttachmentNote } from "../types";
export const TaskComments = ({ taskId }: { taskId: string | number }) => {
  const { identity } = useGetIdentity(),
    notify = useNotify();
  const [text, setText] = useState(""),
    [files, setFiles] = useState<AttachmentNote[]>([]);
  const [create, { isPending: isCreating }] = useCreate(),
    [remove, { isPending: isDeleting }] = useDelete();
  const {
    data: comments = [],
    isPending,
    refetch,
  } = useGetList<TaskComment>("task_comments", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
    filter: { task_id: taskId },
  });
  const add = () => {
    if (!identity || (!text.trim() && !files.length)) return;
    create(
      "task_comments",
      {
        data: {
          task_id: taskId,
          sales_id: identity.id,
          text: text.trim(),
          attachments: files,
        },
      },
      {
        onSuccess: async () => {
          setText("");
          setFiles([]);
          await refetch();
          notify("Комментарий добавлен", { type: "success" });
        },
        onError: () =>
          notify(
            "Не удалось добавить комментарий. Текст и файлы сохранены в форме.",
            { type: "error" },
          ),
      },
    );
  };
  return (
    <section
      className="min-w-0 space-y-4 border-t pt-4"
      aria-label="Комментарии задачи"
    >
      <div>
        <h3 className="text-sm font-medium">Комментарии</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Рабочая переписка и файлы по задаче
        </p>
      </div>
      {!isPending && !comments.length && (
        <p className="text-sm text-muted-foreground">Комментариев пока нет.</p>
      )}
      {comments.map((comment) => (
        <article key={comment.id} className="min-w-0 rounded-xl border p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <ReferenceField
              source="sales_id"
              reference="sales"
              record={comment}
              link={false}
            />
            <DateField source="created_at" record={comment} showDate showTime />
            {(String(comment.sales_id) === String(identity?.id) ||
              identity?.administrator) && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="ml-auto size-7"
                aria-label="Удалить комментарий"
                disabled={isDeleting}
                onClick={() => {
                  if (
                    window.confirm(
                      "Удалить комментарий? Факт удаления останется в истории активности.",
                    )
                  )
                    remove(
                      "task_comments",
                      { id: comment.id, previousData: comment },
                      {
                        onSuccess: async () => {
                          await refetch();
                          notify("Комментарий удалён");
                        },
                        onError: () =>
                          notify("Не удалось удалить комментарий", {
                            type: "error",
                          }),
                      },
                    );
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
          <p className="whitespace-pre-wrap break-words text-sm">
            {comment.text}
          </p>
          <NoteAttachments note={comment} />
        </article>
      ))}
      <MessageComposer
        value={text}
        onChange={setText}
        files={files}
        onFilesChange={setFiles}
        placeholder="Написать комментарий..."
        disabled={isCreating}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={isCreating || (!text.trim() && !files.length)}
          onClick={add}
        >
          Добавить
        </Button>
      </div>
    </section>
  );
};
